import {
  fixEventParams,
  ViewVisibleHelp,
  checkParams,
  handlerErrorParams,
  checkConfig,
  StatsLog,
  getNanoId,
  SingleTask,
} from '@fpkg/stats-utils';
import { BaseEvent, EventIds, PlainObject } from './types';
import { BasePlugin } from './BasePlugin';
import { Bus, Hook, SyncWaterfallHook } from './hook';
import { BaseConfig } from './index';
import { BaseSender } from './BaseSender';

export class BaseStats {
  sdkVersion: string = '';

  config: BaseConfig = new BaseConfig();

  sensors: any = null;

  nanoid: string = ''; // 设备id,清除缓存会重置

  sessionId: string = ''; // 会话id，当前页面浏览id，刷新页面会变

  userId: string = ''; // 用户id，需要业务主动调用login传入

  channelCode: string = ''; // 渠道参数

  // 注册的公共属性
  commonParams: PlainObject = {};

  plugins: Record<string, BasePlugin> = {};

  viewVisibleHelp = new ViewVisibleHelp();

  // 事件订阅，全局通信使用
  $bus = new Bus();

  hooks: Record<string, Hook> = {
    // 上报到神策之前触发hook，可以修改值返回
    beforeSend: new SyncWaterfallHook(),
  };

  sender: BaseSender = new BaseSender();

  singleTask = new SingleTask();

  constructor() {
    this.nanoid = this.createNanoid();
    this.sessionId = this.createSessionid();
  }

  /**
   * 初始化，在应用入口调用
   * @param config
   */
  init(config: BaseConfig) {
    // 检测必传配置项：system_code、product_code、platform_env
    checkConfig(config);

    const startTime = new Date().getTime();

    // 合并配置
    this.config.mergeConfig(config);

    StatsLog.init(this.config.show_log);

    // 初始化渠道号
    this.setChannelCode();
    // 初始化公共参数
    this.initCommonParams();

    // 初始化插件
    this.buildDefaultPlugins();

    // 初始化发送实例
    this.sender.init(this.config, this.commonParams, this.sensors);

    StatsLog.log(`初始化结束，耗时:${new Date().getTime() - startTime}ms`, this);
  }

  /**
   * 初始化通用参数
   */
  initCommonParams() {
    this.registerCommonParams({
      system_code: this.config.system_code, // 项目组编码：由于客渠共用同一个神策项目，用这个区分不同项目组的数据
      product_code: this.config.product_code, // 用来区分前端项目，传git项目名称就可以了
      platform_env: this.config.platform_env, // 平台环境
      app_version: this.config.app_version, // 用来区分前端项目，传git项目名称就可以了
      nanoid: this.nanoid, // 设备id，保存在cookie中，清空浏览器缓存会重新生成新的
      session_id: this.sessionId,
      sdk_version: this.sdkVersion,
      channel_code: this.getChannelCode() || this.getPlatformType(),
      platform_type: this.getPlatformType(), // 平台类型
      platform_name: this.getPlatformName(), // 平台名称
    });
  }

  /**
   * 自定义事件
   * @param event_i:string|{event,event_code,event_name} 事件id,可以传对象
   * @param params:obj 自定义属性,key:字段需采用英文小写字母+”_”或数字的方式命名，长度不超过50位，分段不超过6段
   */
  event(event: string | BaseEvent, params: PlainObject = {}) {
    const eventInfo = fixEventParams(event, params);
    // 校验上报参数合法性
    const newEventInfo = checkParams(eventInfo);
    if (!newEventInfo) return;

    if (newEventInfo.event === 'StatsError') {
      // 内部报错直接上报神策，不执行hooks,防止死循环
      this.sender.sendSensors(newEventInfo);
      return;
    }

    // 业务上报监听执行hooks回调
    let res = newEventInfo;
    try {
      res = this.hooks.beforeSend.call(res);
    } catch (err) {
      console.log(err);
      // 埋点内部报错
      this.customError({
        event: 'StatsError',
        event_code: 'CallHookError',
        event_name: 'c触发钩子函数报错',
        name: 'beforeSend',
        error: err as Error,
      });
    }
    if (res) {
      // 上报神策
      this.sender.sendSensors(res);
    }
  }

  /**
   * 按钮点击事件
   * @deprecated
   * @param params 自定义属性 key:字段需采用英文小写字母+”_”或数字的方式命名，长度不超过50位，分段不超过6段
   */
  btnClick(params: PlainObject = {}): void {
    if (!params.event_code) throw new TypeError('Stats.btnClick:params.event_code!');
    this.event(EventIds.BtnClick, params);
  }

  /**
   *
   * 其他功能事件
   * @deprecated
   * @param params 其他自定义属性，key:字段需采用英文小写字母+”_”或数字的方式命名，长度不超过50位，分段不超过6段
   */
  functionEvent(params: PlainObject = {}): void {
    if (!params.event_code) throw new TypeError('Stats.functionEvent:params.event_code不能为空!');
    this.event(EventIds.FunctionEvent, params);
  }

  /**
   * 弹窗曝光事件,会自动设置visible=true
   * @param params 其他自定义属性，key:字段需采用英文小写字母+”_”或数字的方式命名，长度不超过50位，分段不超过6段
   */
  dialogExposure(params: PlainObject = {}): void {
    if (!params.event_code) throw new TypeError('Stats.dialogExposure:params.event_code不能为空!');
    // 保存弹窗曝光开始时间
    this.viewVisibleHelp.visible(params.event_code as string);
    this.event(EventIds.DialogExposure, params);
  }

  /**
   * 弹窗关闭调用，会自动设置visible=false字段和计算曝光时长(duration)
   * @param params 其他自定义属性，key:字段需采用英文小写字母+”_”或数字的方式命名，长度不超过50位，分段不超过6段
   */
  dialogClose(params: PlainObject = {}): void {
    if (!params.event_code) throw new TypeError('Stats.dialogClose:params.event_code不能为空!');

    const newParams = { ...params };

    newParams.visible = false;
    newParams.duration = this.viewVisibleHelp.invisible(newParams.event_code as string);
    this.event(EventIds.DialogExposure, newParams);
  }

  /**
   * 设置公共属性，不同平台
   * 动态公共属性的时候，注意使用函数类型作为属性值，函数的返回值应当是神策支持的类型，请参考数据格式，否则会被过滤掉。
   * @param params
   */
  registerCommonParams(params: PlainObject = {}): void {
    Object.assign(this.commonParams, params);

    // 不同平台注册神策注册公共属性方法不一致，需要继承修改，web端：registerPage，小程序：registerApp
    this.sensors && this.sensors.registerPage && this.sensors.registerPage(params);
  }

  /**
   * 当用户注册成功或者登录成功时，需要调用 login() 方法传入用户ID
   * @param userId
   */
  login(userId: string): void {
    this.sensors && this.sensors.login(userId);
    this.userId = userId; // apm上报会用到
  }

  /**
   * 设置用户属性
   * @param params
   */
  setUserInfo(params: object) {
    this.sensors && this.sensors.setProfile && this.sensors.setProfile(params);
  }

  /**
   * 页面404事件,不同平台实现方式不同
   * @param params 其他自定义属性，如果需要上报顺丰云日志，设置自定义属性event_level='error'。
   */
  // @ts-ignore
  pageNotFound(params: object = {}): void {
    // todo 需要子类继承
  }

  /**
   * 捕获自定义错误
   * @param params {
   *   event:"",
   *   event_code:"",
   *   error:new Error("")，// error对象，非必传
   * }
   */
  customError(params: PlainObject): void {
    const newParams = { ...params };
    if (!newParams.event) {
      newParams.event = EventIds.CustomError;
    }
    this.event(handlerErrorParams(newParams));
  }

  /**
   * 添加插件
   * @param plugin
   */
  addPlugin(plugin: BasePlugin, pluginOpt: any) {
    // 配置项关闭
    if (pluginOpt === false) return;

    // 如果插件已经添加过，不重复添加
    if (this.plugins[plugin.name]) return;

    this.plugins[plugin.name] = plugin;
    try {
      plugin.setup(this);
    } catch (err) {
      console.error(err);
      // 埋点内部报错
      this.customError({
        event: 'StatsError',
        event_code: 'PluginsInitError',
        event_name: 'Stats插件初始化报错',
        plugin_name: plugin.name,
        error: err as Error,
      });
    }
  }

  /**
   * 获取渠道号
   * @returns {string}
   */
  getChannelCode(): string {
    return this.channelCode;
  }

  /**
   * 设置渠道号，init方法需要调用一次，如果想自定义设置调用此方法
   * @param channelCode 如果为空，会根据url参数自动获取渠道号
   */
  // @ts-ignore
  setChannelCode(channelCode?: string): void {
    // todo 需要子类继承
  }

  /**
   * 获取平台类型
   */
  getPlatformType(): string {
    // todo 需要子类继承
    return '';
  }

  /**
   * 获取平台类型名称
   */
  getPlatformName(): string {
    // todo 需要子类继承
    return '';
  }

  /**
   * 生成设备唯一号，清除缓存会变
   * @private
   */
  createNanoid(): string {
    // todo 需要子类继承
    return '';
  }

  /**
   * 生成会话id，一个浏览器标签算一次
   * @private
   */
  createSessionid(): string {
    return getNanoId();
  }

  /**
   * 构建默认插件
   */
  buildDefaultPlugins() {
    // todo 需要子类继承
  }

  /**
   * 调用钩子函数
   */
  on(hookName: string, callback: any) {
    this.hooks[hookName].tap(callback);
  }

  /**
   * 顺丰云日志告警上报(已废弃)
   * @deprecated
   */
  reportError() {
    console.error("Stats.reportError方法已废弃，请使用event_level='error'来控制事件是否上传顺丰云日志");
  }
}
